import React, { useEffect } from 'react';
import { PageHeader } from '../layout/PageHeader';
import './CoolClimateCalculator.css';

export const CoolClimateCalculator: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    let widgetInstance: any = null;

    const initializeWidget = () => {
      try {
        if (window.Widget && document.getElementById('calculator_container')) {
          // Clean up any existing widget instance
          if (widgetInstance) {
            try {
              // Some widgets have destroy methods
              if (typeof widgetInstance.destroy === 'function') {
                widgetInstance.destroy();
              }
            } catch (e) {
            }
          }

          // Initialize the widget
          widgetInstance = new window.Widget({
            container_id: 'calculator_container',
            campaign: 'carboncalculator',
            source: 'indigo',
            banner_url: false,
            banner_style: {'maxHeight': '0px'},
            highlight_color: "#26aae0",
            hideUserPlatform: true,
            custom_style: 'https://coolclimate-calculator-ui.firebaseapp.com/assets/ipl/ipl.css',
            custom_icon_base: 'https://coolclimate-calculator-ui.firebaseapp.com/assets/ipl/icons/',
            url: 'https://coolclimate-calculator-ui.firebaseapp.com'
          });
          
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing CoolClimate widget:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    const loadScript = () => {
      try {
        // Check if script is already loaded
        if (window.Widget) {
          initializeWidget();
          return;
        }

        // Create and load the script
        script = document.createElement('script');
        script.src = 'https://coolclimate-calculator-ui.firebaseapp.com/assets/widget.min.js?timestamp=1515532767313';
        script.async = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          // Wait a bit for the script to fully initialize
          setTimeout(initializeWidget, 100);
        };
        
        script.onerror = (error) => {
          console.error('Failed to load CoolClimate widget script:', error);
          // Try alternative approach - direct iframe
          tryAlternativeApproach();
        };
        
        // Set a timeout in case the script loads but doesn't initialize properly
        const timeout = setTimeout(() => {
          if (isLoading) {
            tryAlternativeApproach();
          }
        }, 10000); // 10 second timeout
        
        document.head.appendChild(script);
        
        // Clean up timeout if widget initializes successfully
        return () => clearTimeout(timeout);
      } catch (error) {
        console.error('Error loading CoolClimate script:', error);
        tryAlternativeApproach();
      }
    };

    const tryAlternativeApproach = () => {
      try {
        // Alternative: Direct iframe approach
        const container = document.getElementById('calculator_container');
        if (container) {
          // Try multiple iframe sources as fallbacks
          const iframeSources = [
            'https://coolclimate-calculator-ui.firebaseapp.com/',
            'https://coolclimate.org/calculator',
            'https://coolclimate.org/'
          ];
          
          let iframeIndex = 0;
          
          const createIframe = (src: string) => {
            container.innerHTML = `
              <iframe 
                src="${src}" 
                width="100%" 
                height="800" 
                frameborder="0"
                style="border: none; border-radius: 8px; background: transparent;"
                title="CoolClimate Calculator"
                onload="this.style.display='block'"
              ></iframe>
            `;
            
            // Test if iframe loads successfully
            const iframe = container.querySelector('iframe');
            if (iframe) {
              iframe.onload = () => {
                setIsLoading(false);
              };
              
              iframe.onerror = () => {
                iframeIndex++;
                if (iframeIndex < iframeSources.length) {
                  // Try next source
                  setTimeout(() => createIframe(iframeSources[iframeIndex]), 1000);
                } else {
                  // All iframe sources failed
                  setHasError(true);
                  setIsLoading(false);
                }
              };
            }
          };
          
          createIframe(iframeSources[0]);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Alternative approach also failed:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', loadScript);
    } else {
      loadScript();
    }
    
    // Cleanup function
    return () => {
      try {
        // Clean up widget instance
        if (widgetInstance) {
          if (typeof widgetInstance.destroy === 'function') {
            widgetInstance.destroy();
          }
          widgetInstance = null;
        }
        
        // Remove script
        if (script && document.head.contains(script)) {
          document.head.removeChild(script);
        }
      } catch (error) {
      }
    };
  }, [isLoading]);

  return (
    <div className="coolclimate-calculator-page">
      {/* Page Header - Using UC Teal (second color from navigation) */}
      <PageHeader 
        title="CoolClimate Calculator"
        subtitle="Calculate your household carbon footprint and create a personalized climate action plan"
        variant="coolclimate"
      />

      <div className="calculator-content">
        <div className="section-container">
          <div className="calculator-description">
            <h3>Calculate Your Carbon Footprint</h3>
            <p>
              Use the CoolClimate Calculator to assess your household's environmental impact across 
              travel, home energy, food consumption, and shopping habits. Compare your results to 
              similar households and create a personalized climate action plan.
            </p>
          </div>
          
          {/* CoolClimate Calculator iframe container */}
          <div className="calculator-iframe-container">
            {isLoading && (
              <div className="calculator-loading">
                <div className="loading-spinner"></div>
                <p>Loading CoolClimate Calculator...</p>
              </div>
            )}
            
            {hasError && (
              <div className="calculator-error">
                <p>Sorry, the CoolClimate Calculator is temporarily unavailable.</p>
                <p>Please try again later or visit <a href="https://coolclimate.org/" target="_blank" rel="noopener noreferrer">coolclimate.org</a> directly.</p>
                <button 
                  className="retry-button"
                  onClick={() => {
                    setHasError(false);
                    setIsLoading(true);
                    // Reload the page to retry
                    window.location.reload();
                  }}
                >
                  Try Again
                </button>
              </div>
            )}
            
            <div 
              id="calculator_container" 
              className={`calculator-widget ${isLoading || hasError ? 'hidden' : ''}`}
            ></div>
          </div>
          
          <div className="calculator-info">
            <h4>What You'll Get:</h4>
            <ul>
              <li><strong>Personalized Assessment:</strong> Calculate your household carbon footprint</li>
              <li><strong>Benchmark Comparison:</strong> Compare your results to similar households</li>
              <li><strong>Action Planning:</strong> Create a personalized climate action plan</li>
              <li><strong>Category Breakdown:</strong> See emissions by travel, home, food, goods, and services</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Widget type to window object
declare global {
  interface Window {
    Widget: any;
  }
}
